import { useEffect, useState } from "react";
import ConfettiExplosion from "react-confetti-explosion";
import useQuery from "../utils/UseQuery";
import { infoMessage } from "../api/errorHandler";
import {
  showMessageCallback,
  getColorsByExecFlow,
  getMessageConfig,
  ShowMessageConfigModel,
} from "./hyperFlowApi";
import { useHistory } from "react-router-dom";
import "./showMessage.scss";
import ClipLoader from "react-spinners/ClipLoader";
import Modal from "react-modal";
import { TextTransform } from "../magic_forms_2/formComponent/components/TextTransform";

Modal.setAppElement("#root");

const HyperFlowShowMessage = () => {
  let history = useHistory();
  //query params
  const query = useQuery();
  const execFlowId = query.get("execFlowId") ?? "error";
  const code = query.get("code") ?? "error";
  const messageB64 = query.get("mb64") ?? "error";
  const useV2 = query.get("useV2") === "true";
  //State selectors

  //local states
  const [bgColor, setBgColor] = useState(["#ffffff", "#ffffff"]);
  const [primaryTextColor, setPrimaryTextColor] = useState("#ffffff");
  const [primaryButtonColor, setPrimaryButtonColor] = useState("#ffffff");
  const [terciaryTextColor, setTerciaryTextColor] = useState("#ffffff");
  const [logo, setLogo] = useState("");
  const [font, setFont] = useState<string | null>(null);
  const [messageConfig, setMessageConfig] =
    useState<ShowMessageConfigModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [darkMode, setDarkMode] = useState(true);
  const [isExploding, setIsExploding] = useState(false);
  const [isSadFace, setIsSadFace] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  //get colors
  async function getColors() {
    let resp = await getColorsByExecFlow(execFlowId);
    setBgColor([resp.backgroundColor1, resp.backgroundColor2]);
    setPrimaryButtonColor(
      resp.primaryButtonColor ||
        "linear-gradient(180deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0.5) 100%)"
    );
    if (resp.primaryTextColor) {
      setPrimaryTextColor(resp.primaryTextColor);
    } else {
      setPrimaryTextColor(darkMode ? "white" : "black");
    }
    setTerciaryTextColor(resp.tertiaryTextColor || "white");
    setDarkMode(resp.darkMode);
    setLogo(resp.logoUrl || "");
    setFont(resp.font);
  }

  function b64DecodeUnicode(str: string) {
    return decodeURIComponent(
      Array.prototype.map
        .call(atob(str), function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
  }

  function b64_to_utf8(str: string): string {
    console.log(window.atob(str));
    return decodeURIComponent(window.atob(str));
  }

  async function getCurrentMessageF() {
    setLoading(true);
    if (useV2) {
      const messageConfig = await getMessageConfig(code, execFlowId);
      if (messageConfig) {
        await showMessageCallback(code);
        setMessageConfig(messageConfig);
        setIsExploding(messageConfig.hasConfetti);
        setIsSadFace(messageConfig.hasSadFace);
        setModalIsOpen(messageConfig.isPopup);
      } else {
        infoMessage("Ocurrió un error, vamos a devolverte al flujo").then(
          () => {
            history.push("/hyperFlow/continueFlow?code=" + code);
          }
        );
      }
    } else {
      try {
        let msg = b64DecodeUnicode(messageB64);
        await showMessageCallback(code);
        await delay(2000);
        setMessage(msg);
      } catch (e: any) {
        infoMessage(
          "Ocurrió un error, por favor intenta de nuevo en 30 segundos"
        );
        console.log(e);
      }
    }
    setLoading(false);
  }

  function delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async function next() {
    try {
      history.push("/hyperFlow/continueFlow?code=" + code);
    } catch (e: any) {
      console.log(e);
    }
  }

  const getAlign = (align?: "CENTER" | "LEFT" | "RIGHT") => {
    if (messageConfig?.hasConfetti) {
      return "center";
    }
    if (align) {
      if (align === "CENTER") {
        return "center";
      } else if (align === "RIGHT") {
        return "right";
      } else {
        return "left";
      }
    } else {
      return "left";
    }
  };

  useEffect(() => {
    getColors();
    getCurrentMessageF();
  }, []);

  useEffect(() => {
    if (messageConfig && messageConfig.hasConfetti) {
      // set isExploding true and false every 5 seconds
      const interval = setInterval(() => {
        setIsExploding((ev) => !ev);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [messageConfig]);

  const fontDictionary: { [key: string]: string } = {
    default: "'Open Sans', sans-serif",
    sourceSans: "'Source Sans 3', sans-serif",
  };

  const getFontFamily = () => {
    if (font) {
      return fontDictionary[font];
    } else {
      return fontDictionary.default;
    }
  };

  //Render
  return (
    <div
      className='flex justify-center items-center w-screen min-h-screen'
      style={{
        background: `radial-gradient(${bgColor[0]}, ${bgColor[1]})`,
        minHeight: "100vh",
        width: "100%",
        overflow: "auto",
        fontFamily: getFontFamily(),
      }}
    >
      <Modal
        isOpen={modalIsOpen}
        shouldCloseOnEsc={false}
        shouldCloseOnOverlayClick={false}
        className='bg-white rounded-lg place-content-center px-2 py-3 w-[80%] flex flex-col justify-center items-center'
        style={{
          overlay: {
            backgroundColor: "rgba(0,0,0,0.35)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        {isSadFace && (
          <svg
            width='76'
            height='76'
            viewBox='0 0 76 76'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <path
              d='M23.75 33.25C23.082 33.25 22.4635 33.1263 21.8945 32.8789C21.3255 32.6315 20.8307 32.2975 20.4102 31.877C19.9896 31.4564 19.6432 30.9492 19.3711 30.3555C19.099 29.7617 18.9753 29.1432 19 28.5C19 27.832 19.1237 27.2135 19.3711 26.6445C19.6185 26.0755 19.9525 25.5807 20.373 25.1602C20.7936 24.7396 21.3008 24.3932 21.8945 24.1211C22.4883 23.849 23.1068 23.7253 23.75 23.75C24.418 23.75 25.0365 23.8737 25.6055 24.1211C26.1745 24.3685 26.6693 24.7025 27.0898 25.123C27.5104 25.5436 27.8568 26.0508 28.1289 26.6445C28.401 27.2383 28.5247 27.8568 28.5 28.5C28.5 29.168 28.3763 29.7865 28.1289 30.3555C27.8815 30.9245 27.5475 31.4193 27.127 31.8398C26.7064 32.2604 26.1992 32.6068 25.6055 32.8789C25.0117 33.151 24.3932 33.2747 23.75 33.25ZM52.25 23.75C52.918 23.75 53.5365 23.8737 54.1055 24.1211C54.6745 24.3685 55.1693 24.7025 55.5898 25.123C56.0104 25.5436 56.3568 26.0508 56.6289 26.6445C56.901 27.2383 57.0247 27.8568 57 28.5C57 29.168 56.8763 29.7865 56.6289 30.3555C56.3815 30.9245 56.0475 31.4193 55.627 31.8398C55.2064 32.2604 54.6992 32.6068 54.1055 32.8789C53.5117 33.151 52.8932 33.2747 52.25 33.25C51.582 33.25 50.9635 33.1263 50.3945 32.8789C49.8255 32.6315 49.3307 32.2975 48.9102 31.877C48.4896 31.4564 48.1432 30.9492 47.8711 30.3555C47.599 29.7617 47.4753 29.1432 47.5 28.5C47.5 27.832 47.6237 27.2135 47.8711 26.6445C48.1185 26.0755 48.4525 25.5807 48.873 25.1602C49.2936 24.7396 49.8008 24.3932 50.3945 24.1211C50.9883 23.849 51.6068 23.7253 52.25 23.75ZM38 0C41.4883 0 44.8529 0.445312 48.0938 1.33594C51.3346 2.22656 54.3652 3.50065 57.1855 5.1582C60.0059 6.81576 62.5664 8.79492 64.8672 11.0957C67.168 13.3965 69.1471 15.9694 70.8047 18.8145C72.4622 21.6595 73.7363 24.6901 74.627 27.9062C75.5176 31.1224 75.9753 34.487 76 38C76 41.4883 75.5547 44.8529 74.6641 48.0938C73.7734 51.3346 72.4994 54.3652 70.8418 57.1855C69.1842 60.0059 67.2051 62.5664 64.9043 64.8672C62.6035 67.168 60.0306 69.1471 57.1855 70.8047C54.3405 72.4622 51.3099 73.7363 48.0938 74.627C44.8776 75.5176 41.513 75.9753 38 76C34.5117 76 31.1471 75.5547 27.9062 74.6641C24.6654 73.7734 21.6348 72.4994 18.8145 70.8418C15.9941 69.1842 13.4336 67.2051 11.1328 64.9043C8.83203 62.6035 6.85286 60.0306 5.19531 57.1855C3.53776 54.3405 2.26367 51.3223 1.37305 48.1309C0.482422 44.9395 0.0247396 41.5625 0 38C0 34.5117 0.445312 31.1471 1.33594 27.9062C2.22656 24.6654 3.50065 21.6348 5.1582 18.8145C6.81576 15.9941 8.79492 13.4336 11.0957 11.1328C13.3965 8.83203 15.9694 6.85286 18.8145 5.19531C21.6595 3.53776 24.6777 2.26367 27.8691 1.37305C31.0605 0.482422 34.4375 0.0247396 38 0ZM38 71.25C41.043 71.25 43.9746 70.8542 46.7949 70.0625C49.6152 69.2708 52.2624 68.1576 54.7363 66.7227C57.2103 65.2878 59.4616 63.5436 61.4902 61.4902C63.5189 59.4368 65.2506 57.1979 66.6855 54.7734C68.1204 52.349 69.2461 49.7018 70.0625 46.832C70.8789 43.9622 71.2747 41.0182 71.25 38C71.25 34.957 70.8542 32.0254 70.0625 29.2051C69.2708 26.3848 68.1576 23.7376 66.7227 21.2637C65.2878 18.7897 63.5436 16.5384 61.4902 14.5098C59.4368 12.4811 57.1979 10.7493 54.7734 9.31445C52.349 7.87956 49.7018 6.75391 46.832 5.9375C43.9622 5.12109 41.0182 4.72526 38 4.75C34.957 4.75 32.0254 5.14583 29.2051 5.9375C26.3848 6.72917 23.7376 7.84245 21.2637 9.27734C18.7897 10.7122 16.5384 12.4564 14.5098 14.5098C12.4811 16.5632 10.7493 18.8021 9.31445 21.2266C7.87956 23.651 6.75391 26.2982 5.9375 29.168C5.12109 32.0378 4.72526 34.9818 4.75 38C4.75 41.043 5.14583 43.9746 5.9375 46.7949C6.72917 49.6152 7.84245 52.2624 9.27734 54.7363C10.7122 57.2103 12.4564 59.4616 14.5098 61.4902C16.5632 63.5189 18.8021 65.2506 21.2266 66.6855C23.651 68.1204 26.2982 69.2461 29.168 70.0625C32.0378 70.8789 34.9818 71.2747 38 71.25ZM38 47.5C41.4141 47.5 44.6426 48.1927 47.6855 49.5781C50.7285 50.9635 53.4004 52.9427 55.7012 55.5156L52.1387 58.6699C50.3079 56.6165 48.1927 55.0332 45.793 53.9199C43.3932 52.8066 40.7956 52.25 38 52.25C35.2292 52.25 32.6439 52.8066 30.2441 53.9199C27.8444 55.0332 25.7168 56.6165 23.8613 58.6699L20.2988 55.5156C22.5749 52.9674 25.2344 51.0007 28.2773 49.6152C31.3203 48.2298 34.5612 47.5247 38 47.5Z'
              fill={primaryTextColor}
            />
          </svg>
        )}
        <div>
          {messageConfig?.messages.map((message, i) => {
            return (
              <h1
                key={i}
                className='mb-[10px]'
                style={{
                  color: message.color || primaryTextColor,
                  textAlign: getAlign(message.align),
                  fontSize: message.size || "20px",
                  fontWeight: message.fontWeight
                    ? parseInt(message.fontWeight)
                    : 600,
                }}
              >
                <TextTransform
                  text={message.message}
                  style={{
                    linkUnderlined: message.linkUnderlined,
                    linkColor: message.linkColor,
                  }}
                />
              </h1>
            );
          })}
        </div>
        <button
          className='flex justify-center items-center text-[16px] font-semibold min-w-[160px] min-h-[56px] rounded-[30px] mt-8'
          style={{
            background: primaryButtonColor,
            color: terciaryTextColor,
          }}
          onClick={() => {
            next();
          }}
        >
          {messageConfig?.continueMessage}
        </button>
      </Modal>
      <>
        {isExploding && (
          <ConfettiExplosion
            className='absolute bottom-0 right-0 w-[100px] h-full z-50'
            width={1000}
            duration={3000}
            force={0.8}
            particleCount={180}
          />
        )}
      </>
      {loading && (
        <div className='flex flex-col items-center'>
          <h1
            style={{
              fontSize: "26px",
              marginBottom: "50px",
              fontWeight: 600,
              color: primaryTextColor,
            }}
          >
            Espere un momento
          </h1>
          <ClipLoader loading={loading} size={150} color={primaryTextColor} />
        </div>
      )}
      {!loading && useV2 && messageConfig && (
        <div className='flex flex-col justify-evenly items-center w-full h-screen overflow-auto'>
          <img
            alt='logo'
            src={logo}
            style={{ width: "150px", height: "auto" }}
            className='self-start ml-[10%]'
          />
          {isSadFace && (
            <svg
              width='76'
              height='76'
              viewBox='0 0 76 76'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <path
                d='M23.75 33.25C23.082 33.25 22.4635 33.1263 21.8945 32.8789C21.3255 32.6315 20.8307 32.2975 20.4102 31.877C19.9896 31.4564 19.6432 30.9492 19.3711 30.3555C19.099 29.7617 18.9753 29.1432 19 28.5C19 27.832 19.1237 27.2135 19.3711 26.6445C19.6185 26.0755 19.9525 25.5807 20.373 25.1602C20.7936 24.7396 21.3008 24.3932 21.8945 24.1211C22.4883 23.849 23.1068 23.7253 23.75 23.75C24.418 23.75 25.0365 23.8737 25.6055 24.1211C26.1745 24.3685 26.6693 24.7025 27.0898 25.123C27.5104 25.5436 27.8568 26.0508 28.1289 26.6445C28.401 27.2383 28.5247 27.8568 28.5 28.5C28.5 29.168 28.3763 29.7865 28.1289 30.3555C27.8815 30.9245 27.5475 31.4193 27.127 31.8398C26.7064 32.2604 26.1992 32.6068 25.6055 32.8789C25.0117 33.151 24.3932 33.2747 23.75 33.25ZM52.25 23.75C52.918 23.75 53.5365 23.8737 54.1055 24.1211C54.6745 24.3685 55.1693 24.7025 55.5898 25.123C56.0104 25.5436 56.3568 26.0508 56.6289 26.6445C56.901 27.2383 57.0247 27.8568 57 28.5C57 29.168 56.8763 29.7865 56.6289 30.3555C56.3815 30.9245 56.0475 31.4193 55.627 31.8398C55.2064 32.2604 54.6992 32.6068 54.1055 32.8789C53.5117 33.151 52.8932 33.2747 52.25 33.25C51.582 33.25 50.9635 33.1263 50.3945 32.8789C49.8255 32.6315 49.3307 32.2975 48.9102 31.877C48.4896 31.4564 48.1432 30.9492 47.8711 30.3555C47.599 29.7617 47.4753 29.1432 47.5 28.5C47.5 27.832 47.6237 27.2135 47.8711 26.6445C48.1185 26.0755 48.4525 25.5807 48.873 25.1602C49.2936 24.7396 49.8008 24.3932 50.3945 24.1211C50.9883 23.849 51.6068 23.7253 52.25 23.75ZM38 0C41.4883 0 44.8529 0.445312 48.0938 1.33594C51.3346 2.22656 54.3652 3.50065 57.1855 5.1582C60.0059 6.81576 62.5664 8.79492 64.8672 11.0957C67.168 13.3965 69.1471 15.9694 70.8047 18.8145C72.4622 21.6595 73.7363 24.6901 74.627 27.9062C75.5176 31.1224 75.9753 34.487 76 38C76 41.4883 75.5547 44.8529 74.6641 48.0938C73.7734 51.3346 72.4994 54.3652 70.8418 57.1855C69.1842 60.0059 67.2051 62.5664 64.9043 64.8672C62.6035 67.168 60.0306 69.1471 57.1855 70.8047C54.3405 72.4622 51.3099 73.7363 48.0938 74.627C44.8776 75.5176 41.513 75.9753 38 76C34.5117 76 31.1471 75.5547 27.9062 74.6641C24.6654 73.7734 21.6348 72.4994 18.8145 70.8418C15.9941 69.1842 13.4336 67.2051 11.1328 64.9043C8.83203 62.6035 6.85286 60.0306 5.19531 57.1855C3.53776 54.3405 2.26367 51.3223 1.37305 48.1309C0.482422 44.9395 0.0247396 41.5625 0 38C0 34.5117 0.445312 31.1471 1.33594 27.9062C2.22656 24.6654 3.50065 21.6348 5.1582 18.8145C6.81576 15.9941 8.79492 13.4336 11.0957 11.1328C13.3965 8.83203 15.9694 6.85286 18.8145 5.19531C21.6595 3.53776 24.6777 2.26367 27.8691 1.37305C31.0605 0.482422 34.4375 0.0247396 38 0ZM38 71.25C41.043 71.25 43.9746 70.8542 46.7949 70.0625C49.6152 69.2708 52.2624 68.1576 54.7363 66.7227C57.2103 65.2878 59.4616 63.5436 61.4902 61.4902C63.5189 59.4368 65.2506 57.1979 66.6855 54.7734C68.1204 52.349 69.2461 49.7018 70.0625 46.832C70.8789 43.9622 71.2747 41.0182 71.25 38C71.25 34.957 70.8542 32.0254 70.0625 29.2051C69.2708 26.3848 68.1576 23.7376 66.7227 21.2637C65.2878 18.7897 63.5436 16.5384 61.4902 14.5098C59.4368 12.4811 57.1979 10.7493 54.7734 9.31445C52.349 7.87956 49.7018 6.75391 46.832 5.9375C43.9622 5.12109 41.0182 4.72526 38 4.75C34.957 4.75 32.0254 5.14583 29.2051 5.9375C26.3848 6.72917 23.7376 7.84245 21.2637 9.27734C18.7897 10.7122 16.5384 12.4564 14.5098 14.5098C12.4811 16.5632 10.7493 18.8021 9.31445 21.2266C7.87956 23.651 6.75391 26.2982 5.9375 29.168C5.12109 32.0378 4.72526 34.9818 4.75 38C4.75 41.043 5.14583 43.9746 5.9375 46.7949C6.72917 49.6152 7.84245 52.2624 9.27734 54.7363C10.7122 57.2103 12.4564 59.4616 14.5098 61.4902C16.5632 63.5189 18.8021 65.2506 21.2266 66.6855C23.651 68.1204 26.2982 69.2461 29.168 70.0625C32.0378 70.8789 34.9818 71.2747 38 71.25ZM38 47.5C41.4141 47.5 44.6426 48.1927 47.6855 49.5781C50.7285 50.9635 53.4004 52.9427 55.7012 55.5156L52.1387 58.6699C50.3079 56.6165 48.1927 55.0332 45.793 53.9199C43.3932 52.8066 40.7956 52.25 38 52.25C35.2292 52.25 32.6439 52.8066 30.2441 53.9199C27.8444 55.0332 25.7168 56.6165 23.8613 58.6699L20.2988 55.5156C22.5749 52.9674 25.2344 51.0007 28.2773 49.6152C31.3203 48.2298 34.5612 47.5247 38 47.5Z'
                fill={primaryTextColor}
              />
            </svg>
          )}
          {!messageConfig.isPopup && (
            <div>
              {messageConfig.messages.map((message, i) => {
                return (
                  <h1
                    key={i}
                    className='mb-[10px] lg:mx-[20%] mx-[5%]'
                    style={{
                      color: message.color || primaryTextColor,
                      textAlign: getAlign(message.align),
                      fontSize: message.size || "20px",
                      fontWeight: message.fontWeight
                        ? parseInt(message.fontWeight)
                        : 600,
                    }}
                  >
                    <TextTransform
                      text={message.message}
                      style={{
                        linkUnderlined: message.linkUnderlined,
                        linkColor: message.linkColor,
                      }}
                    />
                  </h1>
                );
              })}
            </div>
          )}
          {!messageConfig.isPopup && (
            <button
              className='flex justify-center items-center text-[16px] font-semibold min-w-[160px] min-h-[56px] rounded-[30px]'
              style={{
                background: primaryButtonColor,
                color: terciaryTextColor,
              }}
              onClick={() => {
                next();
              }}
            >
              {messageConfig.continueMessage}
            </button>
          )}
        </div>
      )}
      {!loading && !useV2 && (
        <div className='flex flex-col justify-center items-center w-full h-full'>
          <h1
            className='text-center text-[20px] leading-[22px] font-semibold mb-[10px] mt-[20px]'
            style={{
              color: primaryTextColor,
            }}
          >
            {message}
          </h1>

          <div
            style={{
              marginTop: "40px",
              marginBottom: "10px",
            }}
          ></div>
          <button
            className='flex justify-center items-center text-[16px] font-semibold min-w-[160px] min-h-[56px] rounded-[30px]'
            style={{
              background: primaryButtonColor,
              color: terciaryTextColor,
            }}
            onClick={() => {
              next();
            }}
          >
            Continuar
          </button>
        </div>
      )}
    </div>
  );
};

export default HyperFlowShowMessage;
